import { useEffect } from 'react';

type TickerPageType = 'daily' | 'technicals' | 'fundamentals' | 'analysts' | 'insiders';

interface UseTickerPageMetaOptions {
  tickerSymbol?: string | null;
  tickerName?: string | null;
  pageType: TickerPageType;
}

function getDisplayName(tickerSymbol?: string | null, tickerName?: string | null): string {
  const symbol = (tickerSymbol ?? '').toUpperCase();
  const name = tickerName?.trim();

  if (name && symbol && name.toUpperCase() !== symbol) {
    return `${name} (${symbol})`;
  }

  return symbol || 'Stock';
}

function getMetaForPageType(displayName: string, tickerSymbol: string, pageType: TickerPageType) {
  const symbol = tickerSymbol || displayName;

  switch (pageType) {
    case 'daily':
      return {
        title: `${displayName} - Daily Prices from Stock Table`,
        description: `${symbol} historical prices and trading volumes provided by Stock Table.`,
      };
    case 'technicals':
      return {
        title: `${displayName} - Technical Indicators from Stock Table`,
        description: `${symbol} technical indicators, trend signals, and key levels summarized by Stock Table.`,
      };
    case 'fundamentals':
      return {
        title: `${displayName} - Fundamentals from Stock Table`,
        description: `${symbol} valuation, growth, profitability, balance sheet, and cash flow fundamentals from Stock Table.`,
      };
    case 'analysts':
      return {
        title: `${displayName} - Analyst Views from Stock Table`,
        description: `${symbol} analyst ratings, price targets, and earnings estimates compiled by Stock Table.`,
      };
    case 'insiders':
      return {
        title: `${displayName} - Insider Transactions from Stock Table`,
        description: `${symbol} insider buying and selling activity summarized by Stock Table.`,
      };
    default:
      return {
        title: `${displayName} - Stock Table`,
        description: `${symbol} data and analytics provided by Stock Table.`,
      };
  }
}

function setMetaContent(
  head: HTMLHeadElement,
  attribute: 'name' | 'property',
  value: string,
  content: string
) {
  let el = head.querySelector<HTMLMetaElement>(`meta[${attribute}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, value);
    head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function useTickerPageMeta({ tickerSymbol, tickerName, pageType }: UseTickerPageMetaOptions) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const symbol = (tickerSymbol ?? '').toUpperCase();
    const displayName = getDisplayName(symbol, tickerName ?? undefined);
    const { title, description } = getMetaForPageType(displayName, symbol, pageType);

    const head = document.head || document.getElementsByTagName('head')[0];

    // Set document title
    document.title = title;

    // Update (or create) meta description
    setMetaContent(head, 'name', 'description', description);

    // Open Graph: same title/description for social previews
    setMetaContent(head, 'property', 'og:title', title);
    setMetaContent(head, 'property', 'og:description', description);
    const canonicalUrl = `${window.location.origin}${window.location.pathname}`;
    setMetaContent(head, 'property', 'og:url', canonicalUrl);

    // Twitter Card: same title/description
    setMetaContent(head, 'name', 'twitter:title', title);
    setMetaContent(head, 'name', 'twitter:description', description);
  }, [tickerSymbol, tickerName, pageType]);
}

